/**
 * Text-wrap geometry.
 *
 * Converts page objects into "obstacles" expressed in a text box's content-local
 * coordinate space and answers, for a given line band [y0, y1], which horizontal
 * intervals are blocked. This is what lets body text flow around pictures and
 * shapes (Publisher-style Square / Tight / Through / Top and Bottom wrapping).
 */
import { SHAPE_GEOMETRY } from './shapes';
import { freeformOutline, freeformToD } from './freeform';

const RECT = [[0, 0], [1, 0], [1, 1], [0, 1]];
const outlineCache = new Map();

const ellipsePoints = (n = 40) => Array.from({ length: n }, (_, i) => {
  const a = (i / n) * Math.PI * 2;
  return [0.5 + 0.5 * Math.cos(a), 0.5 + 0.5 * Math.sin(a)];
});

/** Sample an SVG path (0..100 space) into a polyline in unit space. */
const samplePath = (d, n = 56) => {
  if (typeof document === 'undefined') return RECT;
  let svg = null;
  try {
    svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.style.position = 'absolute';
    svg.style.left = '-9999px';
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
    document.body.appendChild(svg);
    const len = path.getTotalLength();
    if (!len || !Number.isFinite(len)) return RECT;
    const pts = [];
    for (let i = 0; i < n; i++) {
      const p = path.getPointAtLength((i / n) * len);
      pts.push([p.x / 100, p.y / 100]);
    }
    return pts;
  } catch {
    return RECT;
  } finally {
    if (svg && svg.parentNode) svg.parentNode.removeChild(svg);
  }
};

/** Outline of an object in unit space (0..1 x 0..1). */
export const getOutline = (obj) => {
  if (obj.type !== 'shape') return RECT;
  if (obj.shapeType === 'freeform' && obj.properties?.freeform?.paths) {
    // Edited freeform: outline follows the current geometry (cache by serialised path)
    const key = `freeform|${freeformToD(obj.properties.freeform).all}`;
    if (outlineCache.has(key)) return outlineCache.get(key);
    const pts = freeformOutline(obj.properties.freeform);
    if (outlineCache.size > 400) outlineCache.clear();
    outlineCache.set(key, pts);
    return pts;
  }
  const key = `${obj.shapeType}|${obj.properties?.flipX ? 1 : 0}|${obj.properties?.flipY ? 1 : 0}`;
  if (outlineCache.has(key)) return outlineCache.get(key);
  const geom = SHAPE_GEOMETRY[obj.shapeType] || SHAPE_GEOMETRY.rectangle;
  let pts = RECT;
  if (geom.kind === 'ellipse') pts = ellipsePoints();
  else if (geom.kind === 'polygon' && geom.points) {
    pts = geom.points.trim().split(/\s+/).map((pair) => pair.split(',').map(Number)).filter((p) => p.length === 2 && p.every(Number.isFinite)).map(([x, y]) => [x / 100, y / 100]);
    if (pts.length < 3) pts = RECT;
  } else if (geom.kind === 'path' && geom.d && !geom.open) pts = samplePath(geom.d);
  if (obj.properties?.flipX) pts = pts.map(([x, y]) => [1 - x, y]);
  if (obj.properties?.flipY) pts = pts.map(([x, y]) => [x, 1 - y]);
  outlineCache.set(key, pts);
  return pts;
};

/* ------------------------------------------------------------------ */
/* Custom wrap points (Publisher "Edit Wrap Points")                    */
/* ------------------------------------------------------------------ */

/** True when the object carries a usable hand-edited wrap outline (unit space, may extend beyond 0..1). */
export const hasCustomWrapPoints = (o) => Array.isArray(o?.properties?.wrapPoints) && o.properties.wrapPoints.length >= 3 && o.properties.wrapPoints.every((p) => Array.isArray(p) && p.length === 2 && p.every(Number.isFinite));

/** Objects that can carry wrap points: anything text wraps around (pictures and shapes). */
export const canEditWrapPoints = (o) => !!o && (o.type === 'image' || o.type === 'shape');

/** Outline used for wrapping: custom points when present, otherwise the automatic shape outline. */
export const wrapOutline = (o) => (hasCustomWrapPoints(o) ? o.properties.wrapPoints : getOutline(o));

/** Ramer–Douglas–Peucker simplification (closed polygon, unit space). */
const simplifyPolygon = (pts, eps = 0.012) => {
  if (pts.length <= 8) return pts;
  const dist = (p, a, b) => {
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const len2 = dx * dx + dy * dy || 1e-9;
    const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2));
    return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
  };
  const rdp = (arr) => {
    if (arr.length < 3) return arr;
    let idx = 0, max = 0;
    for (let i = 1; i < arr.length - 1; i++) { const d = dist(arr[i], arr[0], arr[arr.length - 1]); if (d > max) { max = d; idx = i; } }
    if (max <= eps) return [arr[0], arr[arr.length - 1]];
    const a = rdp(arr.slice(0, idx + 1)), b = rdp(arr.slice(idx));
    return a.slice(0, -1).concat(b);
  };
  // split the ring at its two farthest-apart points so RDP can treat each half as an open chain
  let i0 = 0, i1 = 0, far = -1;
  for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) { const d = Math.hypot(pts[i][0] - pts[j][0], pts[i][1] - pts[j][1]); if (d > far) { far = d; i0 = i; i1 = j; } }
  const ring = pts.slice(i0).concat(pts.slice(0, i0));
  const split = (i1 - i0 + pts.length) % pts.length;
  const half1 = rdp(ring.slice(0, split + 1));
  const half2 = rdp(ring.slice(split).concat([ring[0]]));
  const out = half1.slice(0, -1).concat(half2.slice(0, -1));
  return out.length >= 3 ? out : pts;
};

/**
 * Starting wrap points for an object: its automatic outline pushed outwards by the wrap distance (unit space, so the
 * points follow later resizes), simplified to a manageable number of handles. Ellipses keep 24 evenly spaced handles.
 */
export const initialWrapPoints = (o) => {
  const w = Math.max(1, o.width), h = Math.max(1, o.height);
  const d = Math.max(0, o.properties?.wrapDistance ?? 8);
  let pts = getOutline(o);
  const geom = o.type === 'shape' ? (SHAPE_GEOMETRY[o.shapeType] || SHAPE_GEOMETRY.rectangle) : null;
  if (geom?.kind === 'ellipse') pts = ellipsePoints(24);
  else if (pts.length > 8) pts = simplifyPolygon(pts);
  const kx = (w + 2 * d) / w, ky = (h + 2 * d) / h;
  return pts.map(([u, v]) => [Math.round((0.5 + (u - 0.5) * kx) * 10000) / 10000, Math.round((0.5 + (v - 0.5) * ky) * 10000) / 10000]);
};

/** Cheap checksum so obstacle signatures change when an interior wrap point moves (bbox unchanged). */
const pointsKey = (pts) => pts.length + ':' + pts.reduce((a, [x, y]) => a + x * 7 + y * 13, 0).toFixed(3);

const DEFAULT_PADS = { t: 4, r: 4, b: 4, l: 4 };

export const padsOf = (props = {}) => ({
  t: props.paddingTop ?? props.padding ?? DEFAULT_PADS.t,
  r: props.paddingRight ?? props.padding ?? DEFAULT_PADS.r,
  b: props.paddingBottom ?? props.padding ?? DEFAULT_PADS.b,
  l: props.paddingLeft ?? props.padding ?? DEFAULT_PADS.l,
});

const zOf = (o, all) => (typeof o.layer === 'number' ? o.layer : all.indexOf(o));

/**
 * Compute the obstacles affecting a text box.
 * Publisher rule: only objects stacked IN FRONT of the text box wrap its text.
 * Returned obstacles are polygons in the text box's content-local coordinates
 * (origin = top-left of the padded content area, unrotated text frame).
 */
export const computeWrapObstacles = (text, all = [], pads = padsOf(text.properties)) => {
  if (!text || !all.length) return [];
  const tz = zOf(text, all);
  const tcx = text.x + text.width / 2;
  const tcy = text.y + text.height / 2;
  const trot = -((text.rotation || 0) * Math.PI) / 180;
  const tc = Math.cos(trot), ts = Math.sin(trot);
  const ox = text.x + pads.l, oy = text.y + pads.t;
  const W = text.width - pads.l - pads.r;
  const H = text.height - pads.t - pads.b;
  const out = [];
  for (const o of all) {
    if (!o || o.id === text.id || o.visible === false) continue;
    const ws = o.properties?.wrapStyle;
    if (!ws || ws === 'none') continue;
    if (zOf(o, all) <= tz) continue; // behind (or same) -> does not wrap
    // Hand-edited wrap points already include the wrap distance (they were seeded with it), so no extra offset.
    const custom = hasCustomWrapPoints(o);
    const d = custom ? 0 : Math.max(0, o.properties?.wrapDistance ?? 8);
    // cheap reject: circles around both objects (custom points may reach outside the object box)
    const rO = Math.hypot(o.width, o.height) / 2 + d + (custom ? Math.hypot(o.width, o.height) : 0);
    const rT = Math.hypot(text.width, text.height) / 2;
    if (Math.hypot(o.x + o.width / 2 - tcx, o.y + o.height / 2 - tcy) > rO + rT) continue;

    const pts = custom ? o.properties.wrapPoints : getOutline(o);
    const cx = o.x + o.width / 2, cy = o.y + o.height / 2;
    const r = ((o.rotation || 0) * Math.PI) / 180;
    const cr = Math.cos(r), sr = Math.sin(r);
    // exact outline (the wrap distance is applied as an offset when intervals are computed)
    const poly = pts.map(([u, v]) => {
      const lx = (u - 0.5) * o.width, ly = (v - 0.5) * o.height;
      let px = cx + lx * cr - ly * sr;
      let py = cy + lx * sr + ly * cr;
      if (trot) {
        const dx = px - tcx, dy = py - tcy;
        px = tcx + dx * tc - dy * ts;
        py = tcy + dx * ts + dy * tc;
      }
      return [px - ox, py - oy];
    });
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const [x, y] of poly) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    x0 -= d; y0 -= d; x1 += d; y1 += d;
    if (x1 <= 0 || y1 <= 0 || x0 >= W || y0 >= H) continue;
    out.push({ id: o.id, style: ws, side: o.properties?.wrapSide || 'both', d, poly, bbox: { x0, y0, x1, y1 }, rot: (o.rotation || 0) - (text.rotation || 0), shape: o.shapeType || o.type, key: custom || o.shapeType === 'freeform' ? pointsKey(pts) : '' });
  }
  return out;
};

/** Cheap signature used to keep obstacle arrays referentially stable between renders. */
export const obstaclesSignature = (obs) => obs.map((o) => `${o.id}:${o.style}:${o.side}:${o.d}:${o.shape}:${o.rot.toFixed(1)}:${o.bbox.x0.toFixed(1)},${o.bbox.y0.toFixed(1)},${o.bbox.x1.toFixed(1)},${o.bbox.y1.toFixed(1)}:${o.key || ''}`).join('|');

/** Horizontal extent of a polygon within the band [y0, y1] (null if none). */
const tightExtent = (poly, y0, y1) => {
  let minX = Infinity, maxX = -Infinity;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const [ax, ay] = poly[i];
    const [bx, by] = poly[(i + 1) % n];
    if (ay >= y0 && ay <= y1) { if (ax < minX) minX = ax; if (ax > maxX) maxX = ax; }
    for (const yy of [y0, y1]) {
      if ((ay - yy) * (by - yy) < 0) {
        const t = (yy - ay) / (by - ay);
        const x = ax + t * (bx - ax);
        if (x < minX) minX = x; if (x > maxX) maxX = x;
      }
    }
  }
  return minX === Infinity ? null : [minX, maxX];
};

/** Crossing intervals of a polygon on a horizontal scanline (even-odd). */
const scanIntervals = (poly, y) => {
  const xs = [];
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const [ax, ay] = poly[i];
    const [bx, by] = poly[(i + 1) % n];
    if ((ay <= y && by > y) || (by <= y && ay > y)) xs.push(ax + ((y - ay) / (by - ay)) * (bx - ax));
  }
  if (xs.length < 2 || xs.length % 2) return null;
  xs.sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < xs.length; i += 2) out.push([xs[i], xs[i + 1]]);
  return out;
};

export const mergeIntervals = (ivs) => {
  if (ivs.length < 2) return ivs;
  const s = [...ivs].sort((a, b) => a[0] - b[0]);
  const out = [s[0].slice()];
  for (let i = 1; i < s.length; i++) {
    const last = out[out.length - 1];
    if (s[i][0] <= last[1]) last[1] = Math.max(last[1], s[i][1]);
    else out.push(s[i].slice());
  }
  return out;
};

/** "Through" wrap: text may flow into concave gaps -> union of scanline crossings over the band. */
const throughIntervals = (poly, y0, y1) => {
  const rows = [y0 + (y1 - y0) * 0.2, (y0 + y1) / 2, y0 + (y1 - y0) * 0.8];
  const all = [];
  for (const y of rows) {
    const ivs = scanIntervals(poly, y);
    if (!ivs) return null;
    all.push(...ivs);
  }
  return mergeIntervals(all);
};

/**
 * Blocked x-intervals for one obstacle on the line band [y0, y1].
 * `W` is the text content width (used for the "largest side" rule).
 */
export const blockedIntervals = (ob, y0, y1, W) => {
  const b = ob.bbox;
  if (b.y1 <= y0 || b.y0 >= y1) return [];
  if (ob.style === 'topBottom') return [[-Infinity, Infinity]];
  const d = ob.d || 0;
  let ivs;
  if (ob.style === 'square') ivs = [[b.x0, b.x1]];
  else {
    const ya = y0 - d, yb = y1 + d;
    if (ob.style === 'through') ivs = throughIntervals(ob.poly, ya, yb) || [tightExtent(ob.poly, ya, yb)];
    else ivs = [tightExtent(ob.poly, ya, yb)];
    ivs = mergeIntervals(ivs.filter(Boolean).map(([a, c]) => [a - d, c + d]));
  }
  if (!ivs.length) return [];
  let minX = Infinity, maxX = -Infinity;
  for (const [a, c] of ivs) { if (a < minX) minX = a; if (c > maxX) maxX = c; }
  switch (ob.side) {
    case 'left': return [[minX, Infinity]];
    case 'right': return [[-Infinity, maxX]];
    case 'largest': return minX >= W - maxX ? [[minX, Infinity]] : [[-Infinity, maxX]];
    default: return ivs;
  }
};

/**
 * Free segments of [left, right] after removing blocked intervals.
 * Segments narrower than `minWidth` are dropped (Publisher avoids slivers).
 */
export const freeSegments = (left, right, blocked, minWidth = 0) => {
  if (right - left <= 0) return [];
  const merged = mergeIntervals(blocked.filter((iv) => iv[1] > left && iv[0] < right));
  const segs = [];
  let cur = left;
  for (const [a, c] of merged) {
    if (a > cur) segs.push([cur, Math.min(a, right)]);
    cur = Math.max(cur, c);
    if (cur >= right) break;
  }
  if (cur < right) segs.push([cur, right]);
  return segs.filter(([a, c]) => c - a >= minWidth);
};

/** Bounding-box exclusions (one per side) for the contentEditable fallback while editing. */
export const editExclusions = (obstacles, W) => {
  const found = obstacles.map((o) => {
    const x0 = Math.max(0, o.bbox.x0), x1 = Math.min(W, o.bbox.x1);
    const y0 = Math.max(0, o.bbox.y0);
    const side = o.style === 'topBottom' ? 'full' : o.side === 'left' ? 'right' : o.side === 'right' ? 'left' : (x0 + x1) / 2 < W / 2 ? 'left' : 'right';
    return { side, y: y0, h: Math.max(0, o.bbox.y1 - y0), w: side === 'full' ? W : side === 'left' ? x1 : W - x0, area: (x1 - x0) * (o.bbox.y1 - y0) };
  }).filter((f) => f.w > 0 && f.h > 0).sort((a, b) => b.area - a.area);
  const out = [];
  const left = found.find((f) => f.side === 'left' || f.side === 'full');
  const right = found.find((f) => f.side === 'right' && f !== left);
  if (left) out.push(left);
  if (right) out.push(right);
  return out;
};

/** Publisher "Wrap text" side options. */
export const WRAP_SIDES = [
  ['both', 'Both Sides'],
  ['left', 'Left Only'],
  ['right', 'Right Only'],
  ['largest', 'Largest Side Only'],
];

const rectsOverlap = (a, b) => a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;

/**
 * True when one of `objects` overlaps a text box that is stacked ABOVE it
 * (wrapping would have no effect until the object is brought forward).
 */
export const wrapBlockedByStacking = (objects, all = []) => objects.some((o) => all.some((t) => (t.type === 'textbox' || t.type === 'text') && t.id !== o.id && t.visible !== false && zOf(t, all) > zOf(o, all) && rectsOverlap(o, t)));
